import React from 'react';
import MaterialSelect from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { useField, FieldProps } from 'formik';
import classnames from 'classnames';
import InputLabel from '@material-ui/core/InputLabel';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import Icons from 'components/Icons';

const useStyles = makeStyles(() =>
  createStyles({
    warn: {
      color: '#fff !important',
      '&:before': {
        borderColor: '#ffffff1f'
      },
      '&:after': {
        borderColor: '#fff !important'
      },
      '&:hover': {
        '&:before': {
          borderColor: '#fff !important'
        }
      }
    },
    noUnderline: {
      backgroundColor: 'none !important',
      '&:before': {
        borderBottom: 'none !important'
      },
      '&:after': {
        borderBottom: 'none !important'
      }
    },
    noBackground: {
      '@global': {
        '.MuiSelect-select': {
          '&:focus': {
            backgroundColor: 'transparent'
          }
        }
      }
    },
    inputField: {
      position: 'relative'
    },
    iconBox: {
      position: 'absolute',
      right: 0
    },
    icon: {
      width: '24px',
      height: '24px'
    },
    isWhite: {
      color: '#fff'
    }
  })
);

interface SelectOptionsType {
  value: string | number;
  name: string;
}

interface Props extends FieldProps {
  optionsoptions: SelectOptionsType[];
}

interface SelectFieldProps {
  input: any;
  meta: { touched: boolean; error: boolean; invalid: boolean };
  options: SelectOptionsType[];
  label?: string;
  name: string;
  shrink?: boolean;
  helperText?: string;
  id?: string;
  readOnly?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  className?: string;
  isWhite?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  tooltip?: string;
  tooltipTop?: number;
  onChange?: Function;

  field: any;
  noUnderline?: boolean;
  noBackground?: boolean;
}

const Select = ({
  field,
  label,
  options,
  onChange,
  fullWidth,
  shrink,
  isWhite,
  readOnly,
  variant,
  placeholder,
  className,
  tooltip,
  noUnderline = false,
  noBackground = false,
  tooltipTop = 4
}: SelectFieldProps) => {
  const classes = useStyles();
  const [, meta] = useField(field.name);
  const { error, touched } = meta;
  const isError = typeof error === 'string' && touched;
  return (
    <div className={classes.inputField}>
      <FormControl
        fullWidth={fullWidth}
        variant={variant}
        error={isError}
        className={className}
        id={field.name}
        role="button"
      >
        {label && (
          <InputLabel
            shrink={shrink}
            htmlFor={field.name}
            className={isWhite ? classes.warn : undefined}
          >
            {label}
          </InputLabel>
        )}
        <MaterialSelect
          value={field.value || ''}
          onBlur={field.onBlur}
          onChange={field.onChange}
          IconComponent={({ className }) => (
            <ExpandMoreIcon
              className={classnames(className, isWhite && classes.isWhite)}
            />
          )}
          inputProps={{
            name: field.name,
            id: field.name,
            readOnly,
            onChange: e => !!onChange && onChange((e.target as any).value)
          }}
          input={
            <Input
              onChange={field.onChange}
              classes={{
                // eslint-disable-next-line prettier/prettier
                underline: isWhite ? classes.warn : noUnderline ? classes.noUnderline : undefined,
                root: noBackground ? classes.noBackground : undefined
              }}
              // {...field}
            />
          }
        >
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
          {options.map(({ value, name }: SelectOptionsType) => {
            return (
              <MenuItem key={value} value={value} data-id={`option-${name}`}>
                {name}
              </MenuItem>
            );
          })}
        </MaterialSelect>
      </FormControl>
      <div className={classes.iconBox} style={{ top: tooltipTop }}>
        {isError && (
          <Tooltip title={error || ''} arrow>
            <ErrorOutlineIcon
              style={{ color: isWhite ? 'white' : 'red' }}
              className={classes.icon}
            />
          </Tooltip>
        )}
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <Icons.HelpIcon
              style={{ color: isWhite ? 'white' : 'currentColor' }}
              className={classes.icon}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default Select;
